import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, Empty, Loading } from '../../components/office';
import { deliveriesApi, suppliersApi, stockApi, purchaseOrdersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { qty } from '../../lib/units';

// Deliveries (Back office sweep, Oct 2026). What arrived. Most come in against an order and
// are received from it; what's expected is at the top with Receive beside it. A delivery with
// no order is logged here with its lines, and stock goes up by them. Each card opens to show
// what came.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const day = (iso) => (iso ? new Date(iso.length > 10 ? iso : `${iso}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const todayIso = () => new Date().toISOString().slice(0, 10);
const valueOf = (d) => [...(d.lines || []), ...(d.extras || [])].reduce((t, l) => t + Number(l.received ?? l.quantity ?? 0) * Number(l.received_price ?? l.price ?? 0), 0);
const EMPTY = { supplierId: '', theirRef: '', lines: [] };

function DeliveryCard({ d, open, onToggle, onDelete }) {
  const lines = d.lines || [];
  const extras = d.extras || [];
  const short = lines.filter((l) => l.state === 'short').length;
  return (
    <div className="border-b border-hairline last:border-0" data-testid={`delivery-row-${d.id}`}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-5 py-3 text-left hover:bg-hover-tint">
        <span className="flex min-w-0 items-center gap-2">
          {open ? <ChevronDown size={15} className="shrink-0 text-ink-faint" /> : <ChevronRight size={15} className="shrink-0 text-ink-faint" />}
          <span className="min-w-0">
            <span className="block font-semibold text-ink">{d.supplier_name}</span>
            <span className="block text-[12.5px] text-ink-soft">
              {day(d.received_date || d.created_at)}{d.order_number ? ` · order ${d.order_number}` : ' · no order'}{d.their_ref ? ` · their ref ${d.their_ref}` : ''}{d.received_by ? ` · ${d.received_by}` : ''}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[13px] text-ink-soft">{d.item_count} item{d.item_count === 1 ? '' : 's'}</span>
          {short > 0 ? <Signal tone="warn" dot={false}>{short} short</Signal> : <Signal tone="ok" dot={false}>received</Signal>}
        </span>
        <span className="w-20 text-right font-mono tabular-nums text-ink">{lines.length || extras.length ? money(valueOf(d)) : '—'}</span>
      </button>
      {open && (
        <div className="px-5 pb-4 pl-12">
          {lines.length || extras.length ? (
            <table className="w-full text-[13.5px]">
              <thead><tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                <th className="py-1.5">Item</th>{d.order_id && <th className="px-3">Ordered</th>}<th className="px-3">Came</th><th className="px-3 text-right">Price</th><th className="px-3">Note</th></tr></thead>
              <tbody>
                {[...lines, ...extras.map((e) => ({ ...e, extra: true }))].map((l) => (
                  <tr key={`${l.extra ? 'x' : 'l'}-${l.stock_id}`} className="border-t border-hairline">
                    <td className="py-1.5 text-ink">{l.name || 'Extra'}{l.extra && <span className="ml-1 text-[12px] text-ink-faint">extra</span>}</td>
                    {d.order_id && <td className="px-3 font-mono tabular-nums text-ink-soft">{l.extra ? '—' : qty(l.quantity, l.unit)}</td>}
                    <td className="px-3 font-mono tabular-nums text-ink">{qty(l.received ?? l.quantity, l.unit)}{l.state === 'short' && <Signal tone="warn" dot={false} className="ml-2">short</Signal>}</td>
                    <td className="px-3 text-right font-mono tabular-nums text-ink-soft">{money(l.received_price ?? l.price)}</td>
                    <td className="px-3 text-[12.5px] text-ink-soft">{l.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-[13px] text-ink-soft">Logged as {d.item_count} item{d.item_count === 1 ? '' : 's'}, before deliveries kept their lines.</p>}
          <div className="mt-3 flex items-center justify-between">
            {d.order_id ? <Link to={`/back-office/orders/${d.order_id}`} className="text-[13px] text-verlo hover:underline">Open order {d.order_number}</Link> : <span />}
            {!d.order_id && (
              <Button variant="ghost" size="sm" className="text-signal-stop hover:text-signal-stop" onClick={onDelete} data-testid={`delivery-delete-${d.id}`}>
                Delete{lines.length ? ' and take it off stock' : ''}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DeliveriesPage = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState(null);
  const [expected, setExpected] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stock, setStock] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [logging, setLogging] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    deliveriesApi.list(),
    purchaseOrdersApi.list().catch(() => ({ data: [] })),
  ]).then(([d, o]) => {
    setDeliveries(d.data?.deliveries || []);
    setExpected((o.data || []).filter((x) => x.status === 'sent'));
  }).catch(() => { setDeliveries([]); toast.error("Couldn't load deliveries"); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    suppliersApi.getAll().then((r) => setSuppliers((r.data || []).filter((s) => s.status !== 'inactive'))).catch(() => {});
    stockApi.getAll().then((r) => setStock(r.data || [])).catch(() => {});
  }, []);

  const all = deliveries || [];
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  const thisWeek = all.filter((d) => (d.received_date || '') >= weekAgo);
  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  const supplier = suppliers.find((s) => s.id === form.supplierId);
  const theirs = stock.filter((s) => s.supplier_id === form.supplierId && !form.lines.some((l) => l.stock_id === s.id));
  const rest = stock.filter((s) => s.supplier_id !== form.supplierId && !form.lines.some((l) => l.stock_id === s.id));
  const formTotal = form.lines.reduce((t, l) => t + (Number(l.quantity) || 0) * (Number(l.price) || 0), 0);

  const addLine = (sid) => {
    const s = byId[sid];
    if (s) setForm({ ...form, lines: [...form.lines, { stock_id: s.id, quantity: String(Number(s.pack_size) || 1), price: String(s.cost_per_unit ?? '') }] });
  };
  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  const save = async () => {
    if (!supplier) { toast.error('Who was it from?'); return; }
    const lines = form.lines.filter((l) => Number(l.quantity) > 0);
    if (!lines.length) { toast.error('Add what came'); return; }
    setSaving(true);
    try {
      await deliveriesApi.create({
        supplier_id: supplier.id, supplier_name: supplier.name, their_ref: form.theirRef.trim(),
        lines: lines.map((l) => ({ stock_id: l.stock_id, quantity: Number(l.quantity), price: l.price === '' ? null : Number(l.price) })),
      });
      toast.success(`Delivery from ${supplier.name} logged; stock is up`);
      setLogging(false);
      setForm(EMPTY);
      load();
      stockApi.getAll().then((r) => setStock(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't log the delivery"); } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(d.lines?.length ? `Delete this delivery from ${d.supplier_name}? What it added comes back off stock; the cost it set stays.` : `Delete this delivery from ${d.supplier_name}?`)) return;
    try { await deliveriesApi.delete(d.id); toast.success('Delivery deleted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); }
  };

  return (
    <div className="space-y-5" data-testid="deliveries-page">
      <PageHeader
        title="Deliveries"
        purpose="What arrived, against what was ordered."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => { setForm(EMPTY); setLogging(true); }} data-testid="delivery-add"><Plus size={16} className="mr-2" />Log a delivery</Button>}
      />

      <Stats>
        <Stat label="This week" value={deliveries ? thisWeek.length : null} testId="deliveries-count" />
        <Stat label="Today" value={deliveries ? all.filter((d) => d.received_date === todayIso()).length : null} />
        <Stat label="Came in short" value={deliveries ? thisWeek.filter((d) => (d.lines || []).some((l) => l.state === 'short')).length : null} hint="This week" />
        <Stat label="Value this week" value={deliveries ? money(thisWeek.reduce((t, d) => t + valueOf(d), 0)) : null} />
      </Stats>

      {expected.length > 0 && (
        <section className="rounded-lg border border-hairline bg-white" aria-label="Expected" data-testid="deliveries-expected">
          <p className="border-b border-hairline px-5 py-2.5 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Expected</p>
          {expected.map((o) => (
            <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-5 py-2.5 last:border-0">
              <span><b className="font-semibold text-ink">{o.supplier_name}</b> <span className="text-[13px] text-ink-soft">· {o.number} · {o.lines.length} item{o.lines.length === 1 ? '' : 's'}{o.deliver_on ? ` · due ${day(o.deliver_on)}` : ''}</span></span>
              <Button size="sm" variant="outline" onClick={() => navigate(`/back-office/orders/${o.id}/receive`)}>Receive it</Button>
            </div>
          ))}
        </section>
      )}

      {deliveries === null ? <Loading shape="table" /> : all.length === 0 ? (
        <Empty word="Deliveries" title="Nothing delivered yet" testId="deliveries-empty">Receive an order when it comes in, or log a delivery that came without one.</Empty>
      ) : (
        <section className="rounded-lg border border-hairline bg-white" aria-label="Deliveries">
          {all.map((d) => <DeliveryCard key={d.id} d={d} open={openId === d.id} onToggle={() => setOpenId(openId === d.id ? null : d.id)} onDelete={() => remove(d)} />)}
        </section>
      )}

      <Dialog open={logging} onOpenChange={setLogging}>
        <DialogContent size="sheet" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle>Log a delivery</DialogTitle>
            <DialogDescription>For one that came without an order. One you ordered is received from its order, under Expected.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">From</Label>
                <Select value={form.supplierId} onValueChange={(id) => setForm({ ...form, supplierId: id })}>
                  <SelectTrigger data-testid="delivery-supplier-select"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">Their delivery note or invoice number <span className="text-ink-faint">(optional)</span></Label>
                <Input value={form.theirRef} onChange={(e) => setForm({ ...form, theirRef: e.target.value })} maxLength={60} data-testid="delivery-their-ref" />
              </div>
            </div>
            {form.supplierId && (
              <div className="rounded-lg border border-hairline">
                {form.lines.length > 0 && (
                  <table className="w-full text-[13.5px]">
                    <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                      <th className="px-3 py-2">Item</th><th className="px-3 py-2">Came</th><th className="px-3 py-2">Price each</th><th className="w-8" /></tr></thead>
                    <tbody>
                      {form.lines.map((l, i) => {
                        const s = byId[l.stock_id];
                        return (
                          <tr key={l.stock_id} className="border-b border-hairline last:border-0">
                            <td className="px-3 py-2 text-ink">{s?.name}</td>
                            <td className="px-3 py-2"><div className="flex items-center gap-1.5">
                              <Input type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-9 w-24 font-mono tabular-nums" aria-label={`${s?.name} came`} data-testid="delivery-line-qty-input" />
                              <span className="text-[13px] text-ink-soft">{s?.unit === 'each' ? '' : s?.unit}</span></div></td>
                            <td className="px-3 py-2"><div className="flex items-center gap-1.5"><span className="text-[13px] text-ink-soft">£</span>
                              <Input type="number" min="0" step="0.01" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} className="h-9 w-24 font-mono tabular-nums" aria-label={`${s?.name} price`} /></div></td>
                            <td className="pr-3"><button type="button" className="text-ink-faint hover:text-signal-stop" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })} aria-label={`Take ${s?.name} off`}>×</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <Select value="" onValueChange={addLine}>
                    <SelectTrigger className="h-9 w-72" data-testid="delivery-add-line"><SelectValue placeholder="+ Add what came" /></SelectTrigger>
                    <SelectContent>
                      {theirs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      {rest.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} <span className="text-ink-faint">· not usually {supplier?.name}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.lines.length > 0 && <span className="text-[13.5px] text-ink-soft">Comes to <b className="font-mono text-ink">{money(formTotal)}</b></span>}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogging(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="delivery-save">{saving ? 'Logging…' : 'Log it and add to stock'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveriesPage;
